import { makeStyles } from "@material-ui/core";
import { TransactionReconciledActivity } from "api/activity";
import { Document } from "api/uploads";
import { asCurrency, asTimelessDate, CollapsableSection, Column, Columns, FormInput, isEmbeddedApp, Link, PopoverValue } from "lqx-react";
import * as React from "react";
import { CommentsCollapsable } from "./commentsSection";
import { RemittanceDocumentLink } from "./remittanceDocumentLink";

export const TransactionReconciledDetails = ({ data, attachment, comment, currency }: {
    data: TransactionReconciledActivity,
    attachment: Document,
    comment: string,
    currency: string
}) => {
    const classes = useTextStyles();

    return (
        <>
            <CommentsCollapsable attachment={attachment} comment={comment} />
            <CollapsableSection title="Transaction Details:" notExpanded>
                <Columns>
                    <Column>
                        <FormInput label="Transaction Amount" value={asCurrency(data.transactionDetails.amount, currency)} />
                        <FormInput label="Value Date" value={asTimelessDate(data.transactionDetails.valueDate)} />
                    </Column>
                    <Column>
                        <FormInput label="Statement Date" value={asTimelessDate(data.transactionDetails.statementDate)} />
                        <FormInput label="BAI Code" value={data.transactionDetails.baiCode} />
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection title="Reconciliation Details:" notExpanded>
                {data.isFullyReconciled ?
                    <FullyReconciledDetailsColumns data={data} /> :
                    <PartiallyReconciledDetailsColumns data={data} />}
            </CollapsableSection>
            <CollapsableSection title="Invoices" notExpanded>
                <Columns>
                    <Column>
                        <FormInput label="Total amount applied against Invoices" value={asCurrency(data.invoices.reduce((acc: number, inv) => acc + inv.amount, 0), currency)} />
                    </Column>
                    <Column>
                        <label className={classes.root}>
                            <span className={classes.label}>
                                Number of Invoices
                            </span>
                            <div className={classes.invoiceNumberValue}>
                                <PopoverValue anchorAlign="left" transformAlign="center" withCloseIcon={false} label={data.invoices.length}>
                                    {data.invoices.length ?
                                        <>
                                            {data.invoices.map(inv =>
                                                <div key={inv.cid}>
                                                    <Link disabled={isEmbeddedApp()} href={`/invoices/details/${inv.invoiceId}`}>
                                                        {inv.invoiceNumber}
                                                    </Link>
                                                </div>
                                            )}
                                        </> : undefined}
                                </PopoverValue>
                            </div>
                        </label>
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection title="Linked Remittance Advice:" notExpanded>
                <Columns>
                    <Column>
                        <FormInput label="Remittance Amount" value={asCurrency(data.remittanceAdviceDetails.amount, currency)} />
                        <RemittanceDocumentLink remittanceDocument={data.remittanceAdviceDetails.document} />
                    </Column>
                    <Column>
                        {data.remittanceAdviceDetails && data.remittanceAdviceDetails.remittanceId ?
                            <Link href={`/inmatch/remittance-advices/details/${data.remittanceAdviceDetails.remittanceId}`}>Document Link</Link> :
                            <label className={classes.root}>
                                <span className={classes.label}>
                                    Document Link
                                </span>
                            </label>
                        }
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection title="Customer Details:" notExpanded>
                <Columns>
                    <Column>
                        <FormInput label="Customer Name" value={data.relationshipDetails.customerName} />
                    </Column>
                    <Column>
                        <FormInput label="Customer ID" value={data.relationshipDetails.customerId} />
                    </Column>
                </Columns>
            </CollapsableSection>
            <CollapsableSection title="Supplier Details:" notExpanded>
                <Columns>
                    <Column>
                        <FormInput label="Legal Entity Name" value={data.relationshipDetails.supplierLegalEntityName} />
                    </Column>
                    <Column>
                        <FormInput label="Company Code" value={data.relationshipDetails.supplierCompanyCode} />
                    </Column>
                </Columns>
            </CollapsableSection>
        </>
    );
}

const FullyReconciledDetailsColumns = ({ data }: {
    data: TransactionReconciledActivity
}) => {
    return (
        <Columns>
            <Column>
                <FormInput label="Processing Fee" value={data.reconciliationDetails.processingFee} />
                <FormInput label="Discount (Invoices)" value={data.reconciliationDetails.discount} />
                <FormInput label="Write Off(Invoices)" value={data.reconciliationDetails.writeOff} />
            </Column>
            <Column>
                <FormInput label="Total Amount Applied" value={data.reconciliationDetails.totalAmountApplied} />
                <FormInput label="FX Gain/Loss" value={data.reconciliationDetails.fxGainLoss} />
            </Column>
        </Columns>)
}

const PartiallyReconciledDetailsColumns = ({ data }: {
    data: TransactionReconciledActivity
}) => {
    return (
        <Columns>
            <Column>
                <FormInput label="Amount Remaining" value={data.reconciliationDetails.remainingAmount} />
                <FormInput label="FX Gain/Loss" value={data.reconciliationDetails.fxGainLoss} />
                <FormInput label="Write Off(Invoices)" value={data.reconciliationDetails.writeOff} />
            </Column>
            <Column>
                <FormInput label="Total Amount Applied" value={data.reconciliationDetails.totalAmountApplied} />
                <FormInput label="Processing Fee" value={data.reconciliationDetails.processingFee} />
                <FormInput label="Discount (Invoices)" value={data.reconciliationDetails.discount} />
            </Column>
        </Columns>)
}

const useTextStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        color: '#767D84',
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 500,
        '&:focus-within': {
            color: '#1084D3',
            fontWeight: 600,
        }
    },

    label: {
        flex: 2,
        alignSelf: 'center',
    },

    invoiceNumberValue: {
        flexDirection: 'column',
        flex: 3,
        display: 'inline-flex',
    },
});
